import firebase from "../../firebase/firebase";
import { DB_COLLECTIONS, DB_DEFAULTS } from "../../firebase/collections";
import { mapStand, mapUserToSnippet, mapUserToVisitorSnippet } from "../../utils/mapping";
import { Stand, User, UserSnippet } from "../../data/interfaces";
import { Dispatch } from "redux";
import moment from "moment";
import { USER_ROLES } from "../../data/enums";
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from "../../utils/analytics";

// -----------------------------------------------------------------
// A c t i o n s
// -----------------------------------------------------------------
/**
 * Fetches stands list.
 * @function
 * @async
 *
 * @param limit
 * @param last
 */
export const getStands = (limit?: number, last?: Stand) => async (dispatch: Dispatch<any>) => {
  try {
    dispatch({
      type: "STANDS_LIST_LOADING",
    });

    let output: Stand[] = [];

    // Fixed limit to load almost all stands
    limit = 60;

    // Load stands from firestore
    if (!limit) {
      limit = DB_DEFAULTS.LIMIT;
    }

    console.log("[Firebase read] stands list");
    const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.STANDS);
    let querySnapshot = null;

    // No pagination
    if (!last) {
      querySnapshot = await collectionRef.orderBy("name").limit(limit).get();
    } else {
      // With pagination
      querySnapshot = await collectionRef.orderBy("name").startAfter(last.name).limit(limit).get();
    }

    // Impossible but in case...
    if (!querySnapshot) {
      dispatch({
        type: "STANDS_LIST_LOADED",
        payload: {
          stands: output,
          canLoadMore: false,
        },
      });
      return;
    }

    querySnapshot.forEach((doc:any) => {
      output.push(mapStand(doc.id, doc.data()));
    });

    dispatch({
      type: "STANDS_LIST_LOADED",
      payload: {
        stands: output,
        canLoadMore: output.length >= limit,
      },
    });
    return;
  } catch (err) {
    console.error(err);
    dispatch({
      type: "STANDS_LIST_ERROR",
    });
  }
};

/**
 * Fetches stand detail.
 * @function
 * @async
 *
 * @param id
 */
export const getStandDetail = async (id: string) => {
  try {
    console.log("[Firebase read] stand detail", id);

    // Load stands from firestore
    let querySnapshot = await firebase.firestore().collection(DB_COLLECTIONS.STANDS).doc(id).get();

    return mapStand(querySnapshot.id, querySnapshot.data());
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Fetches team members filtered by stand.
 * @function
 * @async
 *
 * @param idStand
 * @param limit
 * @param last
 */
export const getStandTeam = async (idStand: string, limit?: number, last?: number) => {
  try {
    // Load team from firestore
    if (!limit) {
      limit = DB_DEFAULTS.LIMIT;
    }

    console.log("[Firebase read] stand team", idStand);
    let querySnapshot = await firebase
      .firestore()
      .collection(DB_COLLECTIONS.STANDS)
      .doc(idStand)
      .collection(DB_COLLECTIONS.MEMBERS)
      .where("deleted", "==", false)
      .orderBy("created_at", "desc")
      .limit(limit)
      .get();

    let data: any[] = [];

    querySnapshot.forEach(function (doc) {
      data.push({
        uid: doc.id,
        ...doc.data(),
      });
    });
    return data;
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Search stands from firebase
 * @function
 * @async
 *
 * @param query
 * @param limit
 * @param last
 */
export const searchStands = async (query: string, limit?: number, last?: any): Promise<any> => {
  try {
    if (!query || query.length < 3) {
      return [];
    }

    console.log("[Firebase read] stand search track", query);

    if (!limit) {
      limit = DB_DEFAULTS.LIMIT;
    }

    const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.STANDS);
    let querySnapshot = null;

    // No pagination
    if (!last) {
      querySnapshot = await collectionRef
        .where("keywords", "array-contains", query.toLowerCase())
        .orderBy("name", "asc")
        .limit(limit)
        .get();
    } else {
      // With pagination
      querySnapshot = await collectionRef
        .where("keywords", "array-contains", query.toLowerCase())
        .orderBy("name", "asc")
        .startAfter(last.name)
        .limit(limit)
        .get();
    }

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: any[] = [];

    querySnapshot.forEach((doc:any) => {
      data.push(mapStand(doc.id, doc.data()));
    });

    return data;
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Fetches members busy slots (merged).
 * @function
 * @async
 *
 * @param idStand
 * @param idMembers
 * @param day
 */
export const getMembersBusySlots = async (
  idStand: string,
  idMembers: string[],
  day: string
): Promise<string[]> => {
  try {
    return new Promise((resolve, reject) => {
      let itemRefs = idMembers.map((idMember: string) => {
        console.log("[Firebase read] get busy members slots");

        return firebase
          .firestore()
          .collection(DB_COLLECTIONS.STANDS)
          .doc(idStand)
          .collection(DB_COLLECTIONS.MEMBERS)
          .doc(idMember)
          .collection(DB_COLLECTIONS.BUSY_SLOTS)
          .where("day", "==", day)
          .get();
      });

      Promise.all(itemRefs)
        .then((querySnapshots) => {
          const RES: string[] = [];
          querySnapshots.forEach((querySnapshot) => {
            querySnapshot.docs.forEach((doc) => {
              RES.push(doc.id);
            });
          });
          resolve(RES);
        })
        .catch((error) => console.log(error));
    });
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Update stand team member availability.
 * @function
 * @async
 *
 * @param idStand
 * @param members
 * @param start_datetime
 */
export const updateMembersAvailability = async (
  idStand: string,
  members: UserSnippet[],
  start_datetime: any
) => {
  try {
    return new Promise((resolve, reject) => {
      let itemRefs = members.map((member: UserSnippet) => {
        console.log("[Firebase read] get timing members");

        return firebase
          .firestore()
          .collection(DB_COLLECTIONS.STANDS)
          .doc(idStand)
          .collection(DB_COLLECTIONS.MEMBERS)
          .doc(member.uid)
          .collection(DB_COLLECTIONS.BUSY_SLOTS)
          .doc(`${moment(start_datetime).unix()}`)
          .set(
            {
              day: moment(start_datetime)
                .clone()
                .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
                .toISOString(true),
            },
            { merge: true }
          );
      });

      Promise.all(itemRefs)
        .then(() => {
          resolve();
        })
        .catch((error) => console.log(error));
    });
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Search stands from firebase
 * @function
 * @async
 * @deprecated
 *
 * @param category_id
 * @param limit
 * @param last
 */
export const getStandsByCategory = async (
  category_id: string,
  limit?: number,
  last?: any
): Promise<any> => {
  return [];
  // try {
  //     console.log("[Firebase read] Search stands by category: ", category_id);

  //     if (!category_id) {
  //         return [];
  //     }

  //     if (!limit) {
  //         limit = DB_DEFAULTS.LIMIT;
  //     }

  //     const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.STANDS);
  //     let querySnapshot = null;

  //     // No pagination
  //     if (!last) {
  //         querySnapshot = await collectionRef
  //             .where("categories", "array-contains", category_id)
  //             .orderBy("name", "asc")
  //             .limit(limit)
  //             .get();
  //     } else {
  //         // With pagination
  //         querySnapshot = await collectionRef
  //             .where("categories", "array-contains", category_id)
  //             .orderBy("name", "asc")
  //             .startAfter(last.name)
  //             .limit(limit)
  //             .get();
  //     }

  //     // Impossible but in case...
  //     if (!querySnapshot) {
  //         return [];
  //     }

  //     let data: any[] = [];

  //     querySnapshot.forEach((doc:any) => {
  //         data.push(mapStand(doc.id, doc.data()));
  //     });

  //     return data;
  // } catch (err) {
  //     console.error(err);
  //     throw new Error(err);
  // }
};

/**
 * Updates visitors counter on stand
 * @function
 * @async
 *
 * @param standId
 * @param value
 */
export const updateLiveVisitors = async (standId: string, value: number = 1) => {
  return;
  // const increment = firebase.firestore.FieldValue.increment(value);

  // const standRef = firebase.firestore().collection(DB_COLLECTIONS.LIVE_VISITORS).doc(standId);
  // try {
  //     // Update count
  //     await standRef.set({ count: increment }, { merge: true });

  //     // Add / Remove stand to user subcollection
  //     let userId = firebase.auth().currentUser?.uid;

  //     if (!!userId) {
  //         let userRef = firebase.firestore().collection(DB_COLLECTIONS.USERS).doc(userId);

  //         console.log("[Firebase write] live visitors: ", standId);
  //         if (value > 0) {
  //             await userRef.update({
  //                 visited_stands: firebase.firestore.FieldValue.arrayUnion(standId),
  //             });
  //         } else {
  //             await userRef.update({
  //                 visited_stands: firebase.firestore.FieldValue.arrayRemove(standId),
  //             });
  //         }
  //     }
  // } catch (err) {
  //     console.error(err);
  //     console.error("[Firebase write] live visitors: ", standId);
  // }
};

/**
 * Stores user in stand contacts
 * @function
 * @async
 *
 * @param stand
 * @param user current auth user
 */
export const leaveBusinessCard = async (stand: Stand, user: User) => {
  try {
    // Create a snippet
    const _stand = {
      id: stand.id,
      name: stand.name,
      logo_image_url: stand.logo_image_url,
    };

    if (!user) {
      return;
    }

    // Create a record with combined keys in business_cards
    let combinedKey: string = `${user.uid}_${stand.id}`;

    console.log("[Firebase write] business card");

    await firebase
      .firestore()
      .collection(DB_COLLECTIONS.BUSINESS_CARDS)
      .doc(combinedKey)
      .set(
        {
          left_datetime: firebase.firestore.Timestamp.now(),
          user: mapUserToSnippet(user),
          stand: _stand,
        },
        { merge: true }
      );

    // Track event
    trackAnalyticsEvent(ANALYTICS_EVENTS.BUSINESS_CARD, { from: user.email, to: stand.name });

    return true;
  } catch (err) {
    console.error("[Firebase write] business card");
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Defines if business card was already left by user to stand
 * @function
 * @async
 *
 * @param uid
 * @param standId
 */
export const businessCardLeft = async (uid: string | undefined, standId: string) => {
  try {
    if (!uid) {
      return false;
    }
    // Search doc ref by combined keys
    let combinedKey: string = `${uid}_${standId}`;

    console.log("[Firebase read] business card already left");

    const docRef = firebase.firestore().collection(DB_COLLECTIONS.BUSINESS_CARDS).doc(combinedKey);

    let doc = await docRef.get();

    return !!doc.exists;
  } catch (err) {
    console.error("[Firebase read] business card already left");
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Add the visitor to the stand (save user ref into stand)
 * @function
 * @async
 *
 * @param standId
 * @param user
 */
export const trackVisitor = (standId: string, user: User) => async (dispatch: Dispatch<any>) => {
  if (!user || user.role !== USER_ROLES.VISITOR) {
    return;
  }

  // Check if stand already in user visited stands
  if (user.visited_stands && user.visited_stands.includes(standId)) {
    return;
  }

  console.log("[Firebase write] user visited stands: ", user.uid);
  let userRef = firebase.firestore().collection(DB_COLLECTIONS.USERS).doc(user.uid);
  await userRef.update({
    visited_stands: firebase.firestore.FieldValue.arrayUnion(standId),
  });

  // Get user doc ref inside stand sub collection
  console.log("[Firebase read] track visitor");
  const docRef = firebase
    .firestore()
    .collection(DB_COLLECTIONS.STANDS)
    .doc(standId)
    .collection(DB_COLLECTIONS.VISITORS)
    .doc(user.uid);

  // If user does not exists on visitors, write it
  try {
    const snapshot = await docRef.get();

    if (!snapshot.exists) {
      console.log(
        "[Firebase write] track visitor",
        `${DB_COLLECTIONS.STANDS}/${standId}/${DB_COLLECTIONS.VISITORS}/${user.uid}`
      );
      await docRef.set(mapUserToVisitorSnippet(user));
    }

    let visited = user.visited_stands ? user.visited_stands : [];
    dispatch({
      type: "USER_UPDATE",
      payload: {
        ...user,
        visited_stands: [...visited, standId],
      },
    });
  } catch (err) {
    console.error("[Firebase error] track visitor");
    console.error(err);
  }
};
